import { forwardRef } from 'react';

import {
  TableContainer,
  TableV2,
  type TableContainerProps,
  type TableV2Props,
} from '@frontend/components';

export type ResponseTableProps = Omit<TableContainerProps, 'children'> & {
  children?: React.ReactNode;
  className?: string;
  tableProps?: Omit<TableV2Props, 'className' | 'children'>;
};

/**
 * Base table component for Response.
 * Provides consistent styling for tables rendered in AI responses.
 *
 * Props are spread to the TableContainer by default.
 * Use `tableProps` to pass props specifically to the TableV2 element.
 */
export const ResponseTable = forwardRef<HTMLTableElement, ResponseTableProps>(
  ({ className, children, tableProps, ...containerProps }, ref) => {
    return (
      <TableContainer
        maxW="100%"
        border="1px solid"
        borderColor="separator.border"
        overflowX="auto"
        variant="outline"
        size="sm"
        tabIndex={-1}
        {...containerProps}
      >
        <TableV2
          ref={ref}
          className={className}
          tabIndex={-1}
          sx={{
            'td > div': {
              display: 'contents',
            },
            'th > div': {
              display: 'contents',
            },
            td: {
              minWidth: 'max-content',
              whiteSpace: 'nowrap',
              px: 1,
            },
            th: {
              minWidth: 'max-content',
              whiteSpace: 'nowrap',
              px: 1,
            },
          }}
          {...tableProps}
        >
          {children}
        </TableV2>
      </TableContainer>
    );
  },
);
ResponseTable.displayName = 'ResponseTable';
